import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { PDFDocument } from "pdf-lib";
import logo from "./pdf_text_extraction_tool.png";
import LanguageSwitcher from "./LanguageSwitcher";

function HomePage() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isFileValid, setIsFileValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();

  const validateFile = async (file) => {
    setIsValidating(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const numPages = pdfDoc.getPageCount();
      console.log("Number of pages:", numPages);

      if (numPages > 15) {
        alert("The file exceeds the 15-page limit. Please select another file.");
        setIsFileValid(false);
      } else {
        setIsFileValid(true);
        if (numPages <= 1) {
          await handleUpload(file, numPages);
        } else {
          setShowWarning(true);
          setUploading(false);
        }
      }
    } catch (err) {
      console.error("Error reading the PDF file:", err);
      alert("Could not read the PDF file. Please try with another file.");
      setIsFileValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setIsFileValid(true);
      setShowWarning(false);
      validateFile(file);
    }
  };

  const handleUpload = async (file, numPages) => {
    if (!file || !isFileValid || uploading) return;

    setUploading(true);
    try {
      const base64Content = await fileToBase64(file);

      const response = await axios.post(
        "https://wdprlohxb6.execute-api.us-east-1.amazonaws.com/prod_home/home",
        {
          fileName: file.name,
          fileContent: base64Content,
          contentType: file.type,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const fileURL = response.data.fileURL;

      if (numPages <= 1) {
        navigate("/extracttextpage", { state: { fileURL, numPages } });
      } else {
        navigate("/login", { state: { fileURL, numPages } });
      }
    } catch (error) {
      console.error("Error uploading the file:", error);
      alert("There was an error uploading the file. Please try again.");
    } finally {
      setUploading(false);
      setSelectedFile(null);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f4f7f6] p-6 text-center">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <img src={logo} alt="PDF Text Extraction Tool Logo" className="w-64 md:w-70 mb-6" />

      <h1 className="text-4xl md:text-5xl font-bold mb-2 text-[#3498db]">Hi!</h1>

      <p className="text-lg md:text-xl text-[#2c3e50] mb-6 max-w-md md:max-w-xl px-4">
        Convert your scanned PDF files into text easily and quickly. Just upload your file, and we'll take care of the rest!
      </p>

      <div className="mb-4">
        <input
          id="fileInput"
          type="file"
          onChange={handleFileSelect}
          disabled={uploading || isValidating}
          className="hidden"
        />
        <label
          htmlFor="fileInput"
          className="cursor-pointer px-8 py-3 text-base md:text-lg bg-[#3498db] text-white rounded-xl hover:bg-[#2980b9] transition"
        >
          {selectedFile ? selectedFile.name : "Select file"}
        </label>
      </div>

      {selectedFile && (
        <div className="text-sm text-gray-700 mb-2">{selectedFile.name}</div>
      )}

      <button
        onClick={() => handleUpload(selectedFile)}
        disabled={uploading || !selectedFile || !isFileValid || isValidating}
        className={`px-8 py-3 text-base md:text-lg rounded-xl text-white font-medium transition ${
          uploading || !selectedFile || !isFileValid || isValidating
            ? "bg-[#e67e22] cursor-not-allowed"
            : "bg-[#cf711c] hover:bg-[#cf711c]"
        }`}
      >
        {uploading ? "Uploading..." : "Upload File"}
      </button>

      {showWarning && (
        <div className="mt-4 p-3 text-yellow-800 bg-yellow-100 rounded-lg max-w-md">
          The file has more than 1 page. To continue with the text extraction, please click 'Upload File' and then log in.
        </div>
      )}

      <p className="mt-6 text-sm text-[#3498db]">
        <Link to="/login" className="hover:underline font-medium">
          Log In / Sign Up
        </Link>
      </p>
    </div>
  );
}

export default HomePage;
